<template>
  <div class="container-fluid">
    <listing-action
        :deleteHandler="deleteRecords"
        :createHandler="createRecord"
    ></listing-action>
    <search-items v-model="params.search" :searchHandler="searchTemplate" :placeholder="'Type to search'"></search-items>
    <b-table
        :items="fields"
        :fields="headers"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @sort-changed="sortingChanged"
        sort-icon-left
    >
      <template #cell(id)="{item}">
        <b-form-checkbox
            :id="'checkbox_'+item.id"
            v-model="checkbox"
            name="checkbox-1"
            :value="item.id"
            unchecked-value="not_accepted"
        >
        </b-form-checkbox>
      </template>
      <template #cell(duration_seconds)="{item}">
        {{ item.duration_seconds ? item.duration_seconds + 's' : '—' }}
      </template>
      <template #cell(is_active)="{item}">
        <b-badge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>
      <template #cell(actions)="item">
        <edit class="delete_step ml-2" @click="editVideoTemplate(item.item)" fillColor="#009FE3" />
        <delete class="delete_step ml-2" @click="deleteVideoTemplate(item.item.id)" fillColor="#E30000" />
      </template>
    </b-table>

    <b-pagination
        v-model="pagination.currentPage"
        :total-rows="pagination.rows"
        :per-page="pagination.perPage"
        aria-controls="itemList"
        align="center"
        @change="handlePagination"
        v-if="pagination.total > 0"
    ></b-pagination>

    <b-modal id="addVideoTemplateModal" size="lg" no-close-on-backdrop hide-footer>
      <template #modal-title>
        <span>Video Template</span>
      </template>
      <div class="row">
        <div class="col-md-12" v-if="form.source_template_id">
          <b-alert variant="info" show class="py-2">
            Copied from default template #{{ form.source_template_id }}<span v-if="form.source_version"> (v{{ form.source_version }})</span>.
          </b-alert>
        </div>
        <div class="col-md-12">
          <b-form-group
              label="Template Name"
              labelClass="required"
          >
            <b-form-input
                type="text"
                placeholder="Name"
                v-model="form.name"
                name="name"
                v-validate="{required: true}"
            ></b-form-input>
            <div v-if="errors && errors.first('name')" class="error_msg">
              The name field is required
            </div>
          </b-form-group>
        </div>

        <div class="col-md-12">
          <b-form-group label="Description">
            <b-form-textarea
                placeholder="Description"
                v-model="form.description"
                name="description"
                rows="3"
                max-rows="6"
            ></b-form-textarea>
          </b-form-group>
        </div>

        <div class="col-md-12">
          <b-form-group>
            <template #label>
              Storyline (JSON)
              <small class="text-muted">— scene beats, prompt templates, timeline fragment</small>
            </template>
            <b-form-textarea
                placeholder='{ "scenes": [] }'
                v-model="storylineText"
                name="storyline"
                rows="8"
                max-rows="20"
                :state="storylineValid"
                spellcheck="false"
                class="code_area"
            ></b-form-textarea>
            <div v-if="storylineValid === false" class="error_msg">
              Storyline must be valid JSON.
            </div>
          </b-form-group>
        </div>

        <div class="col-md-12">
          <b-form-group label="Preview Thumbnail URL">
            <b-form-input
                type="text"
                placeholder="https://..."
                v-model="form.preview_thumbnail_url"
                name="preview_thumbnail_url"
            ></b-form-input>
          </b-form-group>
        </div>

        <div class="col-md-6">
          <b-form-group label="Duration (seconds)">
            <b-form-input
                type="number"
                min="1"
                placeholder="e.g. 30"
                v-model="form.duration_seconds"
                name="duration_seconds"
                v-validate="{integer: true, min_value: 1}"
            ></b-form-input>
            <div v-if="errors && errors.first('duration_seconds')" class="error_msg">
              Duration must be a whole number of at least 1
            </div>
          </b-form-group>
        </div>

        <div class="col-md-6">
          <b-form-group label="Sort Order">
            <b-form-input
                type="number"
                placeholder="0"
                v-model="form.sort_order"
                name="sort_order"
                v-validate="{integer: true}"
            ></b-form-input>
            <div v-if="errors && errors.first('sort_order')" class="error_msg">
              Sort order must be a whole number
            </div>
          </b-form-group>
        </div>

        <div class="col-md-12 mt-2">
          <b-form-checkbox v-model="form.is_active" switch>
            Active
          </b-form-checkbox>
        </div>

        <div class="col-12 text-right mt-3">
          <b-button variant="primary" @click="saveVideoTemplate">Save</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>
<script>
import Delete from 'vue-material-design-icons/Delete';
import Edit from 'vue-material-design-icons/CircleEditOutline';
import { http } from "@/config/httpCommon"
import {getLastPage, setPageModal} from "@/helpers/Helpers";
import SearchItems from "@/components/SearchItems.vue";

export default {
  components: {
    Delete,
    Edit,
    SearchItems
  },
  async created(){
    this.$store.commit('set', ['showLoader', true]);
    this.preservePaginationIgnoreSearch('page', 'search', this.$route.name, this.getVideoTemplates);
    this.$store.commit('set', ['showLoader', false]);
  },
  data(){
    return {
      video_templates: [],
      fields: [],
      storylineText: '',
      form: {
        id: 0,
        source_template_id: null,
        source_version: null,
        name: '',
        description: '',
        preview_thumbnail_url: '',
        duration_seconds: null,
        sort_order: 0,
        is_active: true,
      },
      checkbox: [],
      base_url: process.env.VUE_APP_FILE_URL,
      headers: [
        {key: 'id', label: '', sortable: false},
        {key: 'name', label: 'Template Name', sortable: true},
        {key: 'duration_seconds', label: 'Duration', sortable: true},
        {key: 'is_active', label: 'Status', sortable: true},
        {key: 'sort_order', label: 'Sort Order', sortable: true},
        {key: 'actions', label: 'Actions'},
      ],
      pagination:{
        rows:null,
        currentPage:null,
        perPage:null,
        total:0
      },
      sortBy: 'sort_order',
      sortDesc: false,
      params: {
        sortBy: '',
        sortOrder: false,
        search:'',
        page : 1,
      }
    }
  },
  computed: {
    getSortOrder() {
      return this.sortDesc ? 'DESC' : 'ASC'
    },
    getSortField() {
      return this.sortBy
    },
    storylineValid() {
      if (!this.storylineText || !this.storylineText.trim()) return null;
      try { JSON.parse(this.storylineText); return true; } catch (e) { return false; }
    },
  },
  methods:{
    parseStoryline(){
      if (!this.storylineText || !this.storylineText.trim()) return { ok: true, value: null };
      try { return { ok: true, value: JSON.parse(this.storylineText) }; }
      catch (e) { return { ok: false, value: null }; }
    },
    sortingChanged() {
      this.getVideoTemplates()
    },
    searchTemplate(){
      this.params.page = 1;
      setPageModal(1,this.$route.name,this.params);
      this.getVideoTemplates();
    },
    async getVideoTemplates() {
      this.$store.commit('set', ['showLoader', true]);
      this.params.sortBy = this.getSortField
      this.params.sortOrder = this.getSortOrder
      let params = new URLSearchParams(this.params);
      setPageModal(this.params.page,this.$route.name,this.params);
      params = '?'+params.toString();
      let res = await http.get(`video-template`+params);
      this.video_templates = res.data.data
      this.$store.commit('set', ['showLoader', false]);
      this.fields = this.video_templates;
      this.makePagination(res.data);
    },
    async handlePagination(page=''){
      this.params.page = page
      setPageModal(page,this.$route.name,this.params);
      this.getVideoTemplates();
    },
    makePagination(data){
      this.pagination.currentPage = data.meta.current_page;
      this.pagination.rows = data.meta.total;
      this.pagination.perPage = data.meta.per_page;
      this.pagination.total = data.meta.total;
    },
    async saveVideoTemplate(){
      let valid = await this.$validator.validate();
      if(valid){
        let storyline = this.parseStoryline();
        if(!storyline.ok){
          this.showToast('Storyline must be valid JSON', 'DANGER');
          return;
        }
        try {
          this.$store.commit('set', ['showLoader', true]);
          let payload = {
            name: this.form.name,
            description: this.form.description,
            storyline: storyline.value,
            preview_thumbnail_url: this.form.preview_thumbnail_url,
            duration_seconds: this.form.duration_seconds === '' || this.form.duration_seconds === null
                ? null
                : parseInt(this.form.duration_seconds, 10),
            sort_order: this.form.sort_order === '' || this.form.sort_order === null
                ? 0
                : parseInt(this.form.sort_order, 10),
            is_active: Boolean(this.form.is_active),
          }
          if(this.form.id > 0){
            await http.patch(`video-template/${this.form.id}`, payload);
          }else{
            await http.post(`video-template`, payload);
          }
          this.$store.commit('set', ['showLoader', false]);
          this.$bvModal.hide("addVideoTemplateModal");
          if(this.form.id > 0){
            this.preservePagination('page', 'search', this.$route.name, this.getVideoTemplates)
          }else{
            this.nextPageOnCreation('page','search', this.fields.length - 1, this.pagination.perPage, this.pagination.currentPage, getLastPage(this.pagination.perPage, this.pagination.total), this.$route.name, this.getVideoTemplates);
          }
          this.showToast('Video template saved successfully','SUCCESS')
        }catch (err){
          this.$store.commit('set', ['showLoader', false]);
          this.showError(err);
        }
      }
    },
    createRecord(){
      this.form.id = 0;
      this.form.source_template_id = null;
      this.form.source_version = null;
      this.form.name = '';
      this.form.description = '';
      this.form.preview_thumbnail_url = '';
      this.form.duration_seconds = null;
      this.form.sort_order = 0;
      this.form.is_active = true;
      this.storylineText = '';
      this.$nextTick(() => this.$validator.reset());
      this.$bvModal.show("addVideoTemplateModal");
    },
    async deleteRecords(){
      if(this.checkbox.length > 0){
        try {
          let payload = {
            ids: this.checkbox
          }
          this.$confirm('Selected video template/s will be deleted. Are you sure?').then(async ()=>{
            this.$store.commit('set', ['showLoader', true]);
            await http.delete(`video-template/1`,{data: payload });
            this.showToast('Video templates deleted successfully','SUCCESS')
            payload.ids.forEach(id => {
              let index = this.video_templates.findIndex(item => item.id == id);
              this.video_templates.splice(index, 1);
            });
            this.previousPageOnDeletion('page','search',this.fields.length,this.$route.name,this.getVideoTemplates);
            this.$store.commit('set', ['showLoader', false]);
            this.checkbox = [];
          }).catch(_ => {
            console.log(_);
          })
        }catch (e) {
          this.$store.commit('set', ['showLoader', false]);
          this.showErrors(e);
        }
      }else{
        this.showToast('Atleast select 1 template', 'DANGER');
      }
    },
    async deleteVideoTemplate(id){
      try {
        this.$confirm('Video template will be deleted. Are you sure?').then(async ()=>{
          this.$store.commit('set', ['showLoader', true]);
          let payload = {
            ids: [id]
          }
          await http.delete(`video-template/1`,{data: payload });
          this.showToast('Video template deleted successfully','SUCCESS')
          payload.ids.forEach(id => {
            let index = this.video_templates.findIndex(item => item.id == id);
            this.video_templates.splice(index, 1);
          });
          this.previousPageOnDeletion('page','search',this.video_templates.length,this.$route.name,this.getVideoTemplates);
          this.$store.commit('set', ['showLoader', false]);
        }).catch(_ => {
          console.log(_);
        })
      }catch (e) {
        this.showErrors(e);
        this.$store.commit('set', ['showLoader', false]);
      }
    },
    editVideoTemplate(item){
      this.form.id = item.id;
      this.form.source_template_id = item.source_template_id;
      this.form.source_version = item.source_version;
      this.form.name = item.name;
      this.form.description = item.description;
      this.form.preview_thumbnail_url = item.preview_thumbnail_url;
      this.form.duration_seconds = item.duration_seconds;
      this.form.sort_order = item.sort_order;
      this.form.is_active = Boolean(item.is_active);
      this.storylineText = (item.storyline && Object.keys(item.storyline).length)
          ? JSON.stringify(item.storyline, null, 2)
          : '';
      this.$nextTick(() => this.$validator.reset());
      this.$bvModal.show("addVideoTemplateModal");
    }
  }
}
</script>
<style lang="scss" scoped>
.delete_step{
  cursor: pointer;
}
.error_msg{
  color: #E30000;
  font-size: 12px;
  margin-top: 4px;
}
.code_area{
  font-family: monospace;
  font-size: 12px;
}
</style>
